/**
 * Meme coins on the client side.
 *
 * Meme instruments are not in the general instrument list (they have their own section), so they are
 * loaded by symbol when a meme page opens or when the client holds a position or order on one. Their
 * prices come from the price:<SYM> channel and are written into the market store's tickers, so the
 * position tables, portfolio and P/L work for them like for any other market.
 *
 * The meme panel's presets P1–P3 (amounts, leverage, TP / SL, max price move) are kept per client in
 * this browser.
 */
import { useEffect } from 'react';
import { D, fmt, type Instrument, type MemeTradingRules, type Order, type Side, type Ticker } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { ws } from '../lib/ws';
import { marketStore } from './market';
import { sessionStore, useMe } from './session';
import { tradingStore } from './trading';

const pending = new Set<string>();
const live = new Map<string, () => void>();

/** Make sure `symbol` is in the market store; meme instruments also get a live ticker. */
export async function ensureInstrument(symbol: string): Promise<Instrument | null> {
  const have = marketStore.get().bySymbol[symbol];
  if (have) {
    if (have.assetClass === 'MEME') follow(symbol);
    return have;
  }
  if (pending.has(symbol)) return null;
  pending.add(symbol);
  try {
    const inst = await api.get<Instrument>(`/api/instruments/${encodeURIComponent(symbol)}`);
    marketStore.set((s) => ({ bySymbol: { ...s.bySymbol, [symbol]: inst } }));
    if (inst.assetClass === 'MEME') follow(symbol);
    return inst;
  } catch {
    return null;
  } finally {
    pending.delete(symbol);
  }
}

/** Feed price:<SYM> into the tickers (the general ticker snapshot doesn't carry meme coins). */
function follow(symbol: string) {
  if (live.has(symbol)) return;
  const off = ws.subscribe(`price:${symbol}`, (m: { bid: string; ask: string; mark: string; t: number }) => {
    marketStore.set((s) => {
      const prev = s.tickers[symbol];
      const t: Ticker = { s: symbol, bid: m.bid, ask: m.ask, mark: m.mark, t: m.t, open: true, chg24: prev?.chg24 ?? '0', chgAbs24: prev?.chgAbs24 ?? '0', vol24: prev?.vol24 ?? '0', oi: prev?.oi ?? '0' };
      return { tickers: { ...s.tickers, [symbol]: t } };
    });
  });
  live.set(symbol, off);
}

/** Mount once: loads the instrument of any position or order on a market the list doesn't have. */
export function useMemeSync() {
  useEffect(() => {
    const check = () => {
      const { positions, orders } = tradingStore.get();
      const known = marketStore.get().bySymbol;
      if (!marketStore.get().loaded) return;
      for (const sym of new Set([...positions.map((p) => p.symbol), ...orders.map((o) => o.symbol)])) {
        if (!known[sym]) void ensureInstrument(sym);
        else if (known[sym]!.assetClass === 'MEME') follow(sym);
      }
    };
    check();
    const a = tradingStore.subscribe(check);
    const b = marketStore.subscribe(() => {
      if (marketStore.get().loaded) check();
    });
    return () => {
      a();
      b();
    };
  }, []);
}

// ---------- panel presets ----------

export interface MemePreset {
  /** Four quick amounts, USDT. */
  amounts: [string, string, string, string];
  leverage: number;
  tpPct: string | null;
  slPct: string | null;
  /** Max price move for market orders, %. */
  maxDevPct: string;
}

export const DEFAULT_MEME_PRESETS: [MemePreset, MemePreset, MemePreset] = [
  { amounts: ['25', '50', '100', '250'], leverage: 1, tpPct: null, slPct: null, maxDevPct: '1' },
  { amounts: ['50', '100', '250', '500'], leverage: 2, tpPct: '50', slPct: '25', maxDevPct: '1' },
  { amounts: ['100', '250', '500', '1000'], leverage: 3, tpPct: '100', slPct: '30', maxDevPct: '2' },
];

interface PresetState {
  userId: string | null;
  active: 0 | 1 | 2;
  presets: [MemePreset, MemePreset, MemePreset];
}

const presetStore = createStore<PresetState>({ userId: null, active: 0, presets: DEFAULT_MEME_PRESETS });
const key = (userId: string) => `meme-presets:${userId}`;

function load(userId: string) {
  try {
    const raw = JSON.parse(localStorage.getItem(key(userId)) ?? 'null');
    if (raw && Array.isArray(raw.presets) && raw.presets.length === 3) {
      presetStore.set({ userId, active: [0, 1, 2].includes(raw.active) ? raw.active : 0, presets: raw.presets });
      return;
    }
  } catch {
    /* fall through to the defaults */
  }
  presetStore.set({ userId, active: 0, presets: DEFAULT_MEME_PRESETS });
}

export function useMemePresets() {
  const me = useMe();
  const s = useStore(presetStore, (x) => x);
  useEffect(() => {
    if (me && s.userId !== me.id) load(me.id);
  }, [me, s.userId]);
  return s;
}

export function saveMemePresets(next: Pick<PresetState, 'active' | 'presets'>) {
  const userId = presetStore.get().userId;
  presetStore.set(next);
  if (userId) {
    try {
      localStorage.setItem(key(userId), JSON.stringify(next));
    } catch {
      /* private mode: kept for this visit */
    }
  }
}

// ---------- actions ----------

export function activeAccountId(): string | null {
  const s = sessionStore.get();
  return s.me?.accounts.find((a) => a.kind === s.kind)?.id ?? null;
}

export interface MemeOrder {
  symbol: string;
  side: Side;
  type: 'market' | 'limit';
  notional: string;
  leverage: number;
  limitPrice?: string | null;
  tpPrice?: string | null;
  slPrice?: string | null;
  quotedPrice?: string | null;
  maxDeviationPct?: number | null;
}

export async function placeMemeOrder(o: MemeOrder): Promise<Order> {
  const accountId = activeAccountId();
  if (!accountId) throw new Error('No trading account');
  const r = await api.post<{ order: Order }>('/api/orders', { accountId, ...o, channel: 'web' }, { idempotencyKey: crypto.randomUUID() });
  return r.order;
}

export async function closeMemeShare(symbol: string, fraction: string): Promise<number> {
  const accountId = activeAccountId();
  if (!accountId) throw new Error('No trading account');
  const r = await api.post<{ closed: number }>('/api/positions/close-all', { accountId, symbol, fraction, channel: 'web' });
  return r.closed;
}

/** Plain-language trading status for a token. */
export function stateText(r: Pick<MemeTradingRules, 'state' | 'priceFrozen' | 'haltedUntil'>): { label: string; tone: 'up' | 'warn' | 'down' | 'muted'; text: string | null } {
  if (r.state === 'listed' && r.priceFrozen) return { label: 'Paused', tone: 'warn', text: 'Our price sources for this coin disagree, so new positions are paused. Try again shortly.' };
  if (r.state === 'listed') return { label: 'Open', tone: 'up', text: null };
  if (r.state === 'halted') {
    const until = r.haltedUntil ? ` until ${new Date(r.haltedUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';
    return { label: 'Paused', tone: 'warn', text: `Trading is paused${until} after a sharp price move. Positions can't be opened or closed until it resumes.` };
  }
  if (r.state === 'close_only') return { label: 'Close only', tone: 'down', text: 'Close only: unusual activity on this token. You can close or reduce positions, but not open new ones.' };
  if (r.state === 'delisted') return { label: 'Delisted', tone: 'muted', text: 'This coin was delisted. Open positions were closed at the average price of the hour before the event.' };
  return { label: 'Watch only', tone: 'muted', text: 'Not tradable yet. It becomes tradable once it passes our listing checks.' };
}

/** Compact USD amounts for cards: $8.2K, $41M. */
export function compactUsd(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const f = (x: number, s: string) => `$${x >= 100 ? x.toFixed(0) : x.toFixed(1).replace(/\.0$/, '')}${s}`;
  if (abs >= 1e9) return f(n / 1e9, 'B');
  if (abs >= 1e6) return f(n / 1e6, 'M');
  if (abs >= 1e3) return f(n / 1e3, 'K');
  return `$${n.toFixed(n < 1 ? 2 : 0)}`;
}

/** Sub-cent prices: enough significant digits to see moves. */
export function memePrice(v: string | null | undefined, dp?: number): string {
  if (!v) return '—';
  const d = D.from(v);
  const places = dp ?? Math.min(14, Math.max(2, Math.ceil(-Math.log10(Math.max(d.toNumber(), 1e-14))) + 4));
  return `$${fmt(d, places)}`;
}
